<!-- PhoneInput.vue - Reusable phone input component with proper masking -->
<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  prefix: {
    type: String,
    default: '+261',
  },
  label: {
    type: String,
    default: 'Phone',
  },
  placeholder: {
    type: String,
    default: '',
  },
  required: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  errorMessages: {
    type: [String, Array],
    default: '',
  },
  density: {
    type: String,
    default: 'comfortable',
  },
  variant: {
    type: String,
    default: 'outlined',
  },
  hideDetails: {
    type: Boolean,
    default: false,
  },
  inputClass: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['update:modelValue', 'update:prefix'])

// Phone prefix options
const phonePrefixOptions = [
  { title: 'Madagascar (+261)', value: '+261' },
  { title: 'China (+86)', value: '+86' },
]

// Internal reactive values
const currentPrefix = ref(props.prefix)
const currentValue = ref(props.modelValue)

// Computed placeholder based on prefix
const computedPlaceholder = computed(() => {
  if (props.placeholder) return props.placeholder
  return currentPrefix.value === '+261' ? 'XX XX XXX XX' : 'XXX XXXX XXXX'
})

// Phone formatting with proper length enforcement
const formatPhoneNumber = (value, prefix) => {
  let number = value.replace(/\D/g, '')

  if (prefix === '+261') {
    // Enforce maximum length for Madagascar numbers
    if (number.length > 9) number = number.slice(0, 9)
    if (number.length > 0) {
      // Format as XX XX XXX XX
      if (number.length <= 2) return number
      if (number.length <= 4) return `${number.slice(0, 2)} ${number.slice(2)}`
      if (number.length <= 7) return `${number.slice(0, 2)} ${number.slice(2, 4)} ${number.slice(4)}`
      return `${number.slice(0, 2)} ${number.slice(2, 4)} ${number.slice(4, 7)} ${number.slice(7)}`
    }
  } else if (prefix === '+86') {
    // Enforce maximum length for China numbers
    if (number.length > 11) number = number.slice(0, 11)
    if (number.length > 0) {
      // Format as XXX XXXX XXXX
      if (number.length <= 3) return number
      if (number.length <= 7) return `${number.slice(0, 3)} ${number.slice(3)}`
      return `${number.slice(0, 3)} ${number.slice(3, 7)} ${number.slice(7)}`
    }
  }

  return number
}

// Handle keydown events to prevent non-numeric input and enforce length limits
const handleKeyDown = (event) => {
  // Allow: backspace, delete, tab, escape, enter
  if ([8, 9, 27, 13, 46].indexOf(event.keyCode) !== -1 ||
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (event.keyCode === 65 && event.ctrlKey === true) ||
      (event.keyCode === 67 && event.ctrlKey === true) ||
      (event.keyCode === 86 && event.ctrlKey === true) ||
      (event.keyCode === 88 && event.ctrlKey === true) ||
      // Allow: home, end, left, right, down, up
      (event.keyCode >= 35 && event.keyCode <= 40)) {
    return
  }
  
  // Check if this is a numeric key
  const isNumericKey = (event.keyCode >= 48 && event.keyCode <= 57) || (event.keyCode >= 96 && event.keyCode <= 105)
  
  if (isNumericKey) {
    // Remove all non-numeric characters to get clean digit count
    const cleanValue = currentValue.value.replace(/\D/g, '')
    
    // Check length limits based on prefix
    const maxLength = currentPrefix.value === '+261' ? 9 : currentPrefix.value === '+86' ? 11 : 10
    
    // If we're at max length, prevent the keystroke
    if (cleanValue.length >= maxLength) {
      event.preventDefault()
      return
    }
  }
  
  // Ensure that it is a number and stop the keypress if it's not
  if ((event.shiftKey || (event.keyCode < 48 || event.keyCode > 57)) && (event.keyCode < 96 || event.keyCode > 105)) {
    event.preventDefault()
  }
}

// Handle input changes
const handleInput = (e) => {
  const formattedValue = formatPhoneNumber(e.target.value, currentPrefix.value)
  currentValue.value = formattedValue
  emit('update:modelValue', formattedValue)
}

// Handle prefix changes
const handlePrefixChange = (newPrefix) => {
  currentPrefix.value = newPrefix
  // Clear the phone number when prefix changes
  currentValue.value = ''
  emit('update:prefix', newPrefix)
  emit('update:modelValue', '')
}

// Watch for external changes
watch(() => props.modelValue, (newValue) => {
  if (newValue !== currentValue.value) {
    currentValue.value = formatPhoneNumber(newValue, currentPrefix.value)
  }
})

watch(() => props.prefix, (newPrefix) => {
  if (newPrefix !== currentPrefix.value) {
    currentPrefix.value = newPrefix
  }
})
</script>

<template>
  <div>
    <VLabel v-if="label" class="mb-1">
      {{ label }}
      <span v-if="required" class="text-error">*</span>
    </VLabel>
    <VRow no-gutters>
      <VCol cols="auto" style="width: 180px" class="pr-1">
        <VSelect
          :model-value="currentPrefix"
          :items="phonePrefixOptions"
          item-title="title"
          item-value="value"
          :variant="variant"
          :density="density"
          hide-details
          class="rounded-e-0 phone-prefix-dropdown"
          :disabled="disabled || readonly"
          @update:model-value="handlePrefixChange"
        />
      </VCol>
      <VCol>
        <VTextField
          :model-value="currentValue"
          :placeholder="computedPlaceholder"
          :variant="variant"
          :density="density"
          :error-messages="errorMessages"
:class="`rounded-s-0 phone-input ${inputClass}`"
          :required="required"
          :readonly="readonly"
          :disabled="disabled"
          :hide-details="hideDetails"
          @input="handleInput"
          @keydown="handleKeyDown"
        />
      </VCol>
    </VRow>
  </div>
</template>

<style scoped>
.phone-prefix-dropdown :deep(.v-field__input) {
  padding-inline-end: 8px !important;
}

.phone-input :deep(.v-field__input) {
  padding-inline-start: 12px !important;
}
</style>
